import { TagOption, TaxonomyStatus } from "@/lib/schema";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Archive,
  CheckCircle2,
  Lock,
  ChevronDown,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_CONFIG: Record<
  TaxonomyStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
};

const SYSTEM_GROUP_DESCRIPTIONS: Record<string, string> = {
  "tg-cycle-phase":
    "Hormonal cycle phases used for content personalization. Options are system-protected.",
  "tg-concentration-label":
    "Focus areas and wellness intentions for content categorization. Options are system-protected.",
  "tg-content-type-label":
    "Content format types used to classify media and experiences. Options are system-protected.",
  "tg-shift-label":
    "Energy shift intentions describing the transformation each content delivers. Options are system-protected.",
};

export interface SystemGroupCardProps {
  group: { id: string; name: string; description?: string | null };
  options: Array<TagOption>;
  onEditOption: (opt: any) => void;
  onCreateOption: () => void;
  onArchiveOption: (id: string) => void;
  onRestoreOption: (id: string) => void;
  isAdmin: boolean;
}

export function SystemGroupCard({
  group,
  options,
  onEditOption,
  onCreateOption,
  onArchiveOption,
  onRestoreOption,
  isAdmin,
}: SystemGroupCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const description = group.description || SYSTEM_GROUP_DESCRIPTIONS[group.id];

  return (
    <Card data-testid={`group-${group.id}`}>
      <CardContent className="p-4 space-y-1">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="flex items-center gap-3">
            <CollapsibleTrigger asChild>
              <button
                className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
                data-testid={`trigger-group-${group.id}`}
              >
                <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-medium text-foreground"
                      data-testid={`text-group-name-${group.id}`}
                    >
                      {group.name}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                    >
                      {options.length} options
                    </Badge>
                  </div>
                  {description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {description}
                    </p>
                  )}
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="space-y-2 mt-3">
              {options.map((opt) => {
                const optStatus = STATUS_CONFIG[opt.status];
                return (
                  <div
                    key={opt.id}
                    className="flex items-center gap-3 p-3 rounded-md border border-border"
                    data-testid={`option-${opt.id}`}
                  >
                    <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium text-foreground"
                        data-testid={`text-option-label-${opt.id}`}
                      >
                        {opt.label}
                      </p>
                      {opt.description && (
                        <p className="text-xs text-muted-foreground">
                          {opt.description}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant={optStatus.variant}
                      className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                    >
                      {optStatus.label}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`button-option-actions-${opt.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditOption(opt)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {opt.status === "active" && isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onArchiveOption(opt.id)}
                              className="text-destructive"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          </>
                        )}
                        {opt.status === "archived" && isAdmin && (
                          <DropdownMenuItem
                            onClick={() => onRestoreOption(opt.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Restore
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={onCreateOption}
                className="mt-2"
                data-testid={`button-add-option-${group.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Option
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
